/*
 * Copyright (c) 2020
 * Company: G-Portal
 * Filename: actions.ts
 * Class: actions.ts
 * Author: Karl Magerl<karl@g-portal.com>
 * Version:
 */

import { ActionTree } from 'vuex';
import { AxiosError, AxiosResponse } from 'axios';
import {
  AdditionalPaymentDetailsRequest,
  AdditionalPaymentDetailsResponse,
  CreatePaymentRequest,
  CreatePaymentResponse,
  PaymentState,
  LagoCreatePaymentRequest,
  LagoCreatePaymentResponse,
  VerifyPaymentRequest,
  LagoPaymentStatusRequest,
  LagoPaymentStatusResponse,
  PaymentStates,
} from './types';
import { RootState } from '../types';
import { ApiEndpointEnum, getApiRequestConfig } from '../../config/network';
import { PaymentStorageService } from '../../service/payment/paymentStorage';

export const actions: ActionTree<PaymentState, RootState> = {
  createPayment(
    { commit, getters },
    payload: CreatePaymentRequest,
  ): Promise<CreatePaymentResponse> {
    // @TODO: check if we should throw / catch a dedicated expection (https://rollbar.com/guides/javascript-throwing-exceptions/)
    if (getters.createPaymentLoading === true) {
      throw new Error('The action createPaymentLoading is already running!');
    }

    commit('createPaymentLoading');

    return new Promise((resolve, reject) => {
      this.state.$http.post('createPayment', payload, getApiRequestConfig(ApiEndpointEnum.PaymentAPI))
        .then((response: AxiosResponse<CreatePaymentResponse>) => {
          const res = response && response.data;

          PaymentStorageService.create({
            id: res.paymentId,
            mode: payload.mode,
            amount: payload.price.amount,
            currency: payload.price.currency,
            tracked: false,
          });

          commit('createPaymentSuccess', res);
          resolve(res);
        }, (error: AxiosError) => {
          commit('createPaymentError', {
            code: error.code,
            message: error.response.data.message,
            data: null,
          });

          reject();
        });
    });
  },
  getPayment({ commit }, payload: VerifyPaymentRequest): void {
    commit('getPaymentLoading');

    this.state.$http.get(`payment/${payload.paymentId}`, getApiRequestConfig(ApiEndpointEnum.PaymentAPI))
      .then(
        (res) => commit('getPaymentLoaded', res && res.data),
        (err) => commit('getPaymentError'),
      );
  },
  verifyPayment({ commit }, payload: VerifyPaymentRequest): Promise<void> {
    commit('verifyLoading');

    return new Promise((resolve, reject) => {
      this.state.$http.get(`verify/${payload.paymentId}`, getApiRequestConfig(ApiEndpointEnum.PaymentAPI))
        .then((res) => {
          const verification = res && res.data;
          commit('verifySuccess', verification);
          resolve(verification);
        })
        .catch((err) => {
          commit('verifyError', err.response);
          reject(err);
        });
    });
  },
  submitAdditionalDetails(
    { commit },
    payload: AdditionalPaymentDetailsRequest,
  ): Promise<AdditionalPaymentDetailsResponse> {
    commit('additionalPaymentDetailsLoading');

    return new Promise((resolve, reject) => {
      this.state.$http.post('additionalPaymentDetails', payload, getApiRequestConfig(ApiEndpointEnum.PaymentAPI))
        .then((response: AxiosResponse<AdditionalPaymentDetailsResponse>) => {
          const additionalPaymentDetails = response && response.data;
          commit('additionalPaymentDetailsSuccess', additionalPaymentDetails);
          resolve(additionalPaymentDetails);
        }, (error: AxiosError) => {
          commit('additionalPaymentDetailsError', error.response);
          reject(error);
        });
    });
  },
  disableSavedPaymentMethod({ commit }): Promise<void> {
    commit('disableSavedPaymentMethodLoading');

    return new Promise<void>((resolve, reject) => {
      this.state.$http('disableMethod', getApiRequestConfig(ApiEndpointEnum.PaymentAPI))
        .then((response: AxiosResponse<void>) => {
          commit('disableSavedPaymentMethodLoaded');
          resolve();
        }, (error) => {
          commit('disableSavedPaymentMethodError');
          reject();
        });
    });
  },

  // stripe specific actions
  async stripeSetupIntent({ commit }): Promise<string> {
    const { data, status, statusText } = await this.state.$http.post<{ intent: { client_secret: string } }>(`/${window.regionPrefix}/payment/stripe/setupIntent`);

    if (status === 200
      && data != null && data.intent != null && data.intent.client_secret != null
    ) {
      return data.intent.client_secret;
    }

    if (statusText != null) {
      throw new Error(statusText);
    }

    return null;
  },

  async lagoCreatePayment({ commit }, {
    amount,
    description,
  }: LagoCreatePaymentRequest): Promise<LagoCreatePaymentResponse> {
    commit('createPaymentLoading');

    try {
      const { data, status, statusText } = await this.state.$http.post<LagoCreatePaymentResponse>(`/${window.regionPrefix}/payment/lago/makePayment`, undefined, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        params: {
          amount,
          description,
        },
      });

      if (status === 200) {
        return data;
      }

      if (statusText != null) {
        commit('createPaymentError', new Error(statusText));
        return null;
      }
    } catch (error) {
      commit('createPaymentError', error);
      return null;
    }

    commit('createPaymentError', new Error('Unknown error'));
    return null;
  },
  async lagoCheckPayment({ commit },
    paymentId: LagoPaymentStatusRequest): Promise<LagoPaymentStatusResponse> {
    try {
      const { data, status, statusText } = await this.state.$http.get<LagoPaymentStatusResponse>(`/${window.regionPrefix}/payment/lago/paymentStatus`, {
        params: {
          payment_id: paymentId,
        },
      });

      if (status === 200) {
        if (data.payment.status === PaymentStates.FAILURE) {
          commit('createPaymentError', new Error('Payment failed'));

          return null;
        }

        return data;
      }

      if (statusText != null) {
        commit('createPaymentError', new Error(statusText));
        return null;
      }
    } catch (error) {
      commit('createPaymentError', error);
      return null;
    }

    commit('createPaymentError', new Error('Unknown error'));
    return null;
  },
};
